import React, { useState, KeyboardEvent, ChangeEvent, useId } from 'react';
import { Icon } from '@/shared/icons'
import { stripInvisibleChars } from '@/shared/utils/invisibleChars';
interface TagInputProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  type?: 'text' | 'email' | 'tel';
  validation?: (value: string) => string | null;
  disabled?: boolean;
  maxItems?: number;
  required?: boolean;
  /** Prefixo dos data-testids (`{testid}-input`, `{testid}-remove-{i}`) — F42 */
  'data-testid'?: string;
}

/**
 * TagInput - Componente de input com tags inline
 *
 * Padrão moderno de UI/UX para arrays de strings.
 * Tags são criadas ao pressionar Enter, vírgula ou Tab.
 * Cada tag tem um botão FiX para remover.
 *
 * @example
 * <TagInput
 *   label="Emails"
 *   values={emails}
 *   onChange={setEmails}
 *   placeholder="Digite um email e pressione Enter"
 *   type="email"
 *   validation={validateEmail}
 * />
 */
const TagInput: React.FC<TagInputProps> = ({
  label,
  values,
  onChange,
  placeholder = 'Digite e pressione Enter',
  icon,
  type = 'text',
  validation,
  disabled = false,
  maxItems,
  required = false,
  'data-testid': dataTestId,
}) => {
  const uniqueId = useId();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error) setError(null);
  };

  const addTag = (value: string) => {
    // Texto colado de outro aplicativo costuma trazer marcas invisíveis
    // grudadas (o Contatos do macOS/iPhone embrulha telefone em U+202A/U+202C).
    // `trim()` não as remove, e elas reprovavam número correto na validação.
    const trimmedValue = stripInvisibleChars(value).trim();

    if (!trimmedValue) {
      return;
    }

    // Check max items
    if (maxItems && values.length >= maxItems) {
      setError(`Máximo de ${maxItems} itens permitidos`);
      return;
    }

    // Check duplicates
    if (values.includes(trimmedValue)) {
      setError('Este item já foi adicionado');
      return;
    }

    // Validate if validation function provided
    if (validation) {
      const validationError = validation(trimmedValue);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    // Add tag
    onChange([...values, trimmedValue]);
    setInputValue('');
    setError(null);
  };

  const removeTag = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Add tag on Enter, comma, or Tab
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addTag(inputValue);
    }

    // Remove last tag on Backspace if input is empty
    if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      removeTag(values.length - 1);
    }
  };

  const handleBlur = () => {
    // Add tag on blur if there's a value
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Tags Container + Input - Layout Horizontal (Padrão ArrayInput) */}
      <div
        className={`w-full min-h-[42px] px-3 py-2 border rounded-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
      >
        <div className="flex items-center gap-2">
          {/* Icon à esquerda */}
          {icon && <div className="text-gray-400 flex-shrink-0">{icon}</div>}

          {/* Container de Tags + Input (flex wrap para quebra automática) */}
          <div className="flex-1 flex flex-wrap items-center gap-2">
            {/* Tags Display */}
            {values.map((value, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-md"
              >
                {value}
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                  disabled={disabled}
                  aria-label="Remover"
                  data-testid={dataTestId ? `${dataTestId}-remove-${index}` : undefined}
                >
                  <Icon name="x" className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Input Field */}
            <input
              type={type}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={values.length === 0 ? placeholder : ''}
              disabled={disabled || (maxItems !== undefined && values.length >= maxItems)}
              className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              name={`tag-input-${uniqueId}`}
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              data-testid={dataTestId ? `${dataTestId}-input` : undefined}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default TagInput;
